import { useWallet } from '@solana/wallet-adapter-react';
import type { Dapp } from '@src/dto/Dapp';
import { isRight } from 'fp-ts/Either';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { useNavigationHistory } from '../../hooks/useNavigationHistory';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import {
  getVerificationFlags,
  VERIFICATION_DEADLINE,
} from '../../lib/verificationStatus';
import { captureAndReportError } from '../../utils/errorReporting';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import {
  getDappRouteSegmentFromPath,
  resolveVisibleDappFromRouteSegment,
} from '../../utils/dappRoutes';
import { hasDappStaffAccess } from '../../utils/roleUtils';
import { SOLANA_CLUSTER } from '../../utils/solanaUtils';
import { ImportDappDialog } from '../ImportDappDialog';
import type { SidebarVerificationCard } from './KybStatusSection';
import { SettingsView } from './SettingsView';
import { useSidebarData } from './hooks/useSidebarData';
import { SidebarMainContent } from './SidebarMainContent';

export interface SidebarProps {
  className?: string;
  onLinkClick?: () => void;
}

export const Sidebar = ({ className, onLinkClick }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [authState, { signOut }] = useAuth();
  const {
    activePublisher,
    accessiblePublishers,
    isSwitching,
    switchPublisher,
  } = usePublisher();
  const user =
    authState.type === AuthStateType.Authenticated ? authState.user : null;
  const trpc = useTRPC();
  const {
    data: {
      publisherUserJoinId: sidebarPublisherUserJoinId,
      kybStatusData,
      dapps,
    },
    isLoading: isSidebarDataLoading,
  } = useSidebarData();
  const navigationHistory = useNavigationHistory();
  const { connected, publicKey } = useWallet();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [skrClaimed, setSkrClaimed] = useState(false);
  const [walletEligible, setWalletEligible] = useState(false);
  const [expandedDappId, setExpandedDappId] = useState<string | null>(null);
  const canUseStaffDappRoutes = user
    ? hasDappStaffAccess(user.role, user.email)
    : false;
  const hasPublisherProfile = Boolean(activePublisher);
  const [isAddDappDropdownOpen, setIsAddDappDropdownOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [routeDapp, setRouteDapp] = useState<Dapp | null>(null);

  const isOnSettingsPage = currentPath.includes('/settings/');

  const [isSettingsMode, setIsSettingsMode] = useState(isOnSettingsPage);
  const [hasInitialized, setHasInitialized] = useState(false);

  const resolveDappIdFromPath = (
    path: string,
    candidateDapps: Array<{ id: string; packageName?: string | null }>,
  ): string | null => {
    const segment = getDappRouteSegmentFromPath(path);
    const dapp = resolveVisibleDappFromRouteSegment(segment, candidateDapps);
    return dapp?.id ?? null;
  };

  const isActiveRoute = (path: string) => {
    if (currentPath === path) {
      return true;
    }

    return !/^\/dapp\/[^/]+$/.test(path) && currentPath.startsWith(`${path}/`);
  };
  const isSidebarDataCurrent =
    sidebarPublisherUserJoinId ===
    (activePublisher?.publisherUserJoinId ?? null);
  const isLoadingData =
    isSidebarDataLoading || (Boolean(activePublisher) && !isSidebarDataCurrent);
  const visibleDapps = isSidebarDataCurrent ? dapps : [];
  const currentKybStatusData = isSidebarDataCurrent ? kybStatusData : null;
  const routeDappAlreadyVisible =
    routeDapp && visibleDapps.some((dapp) => dapp.id === routeDapp.id);
  const visibleRouteDapp = routeDappAlreadyVisible ? null : routeDapp;
  const publisherKybStatus =
    currentKybStatusData?.status ?? activePublisher?.kybStatus;
  const publisherVerification = getPublisherVerificationGate({
    status: publisherKybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const primaryDapps = useMemo(
    () =>
      visibleRouteDapp ? [visibleRouteDapp, ...visibleDapps] : visibleDapps,
    [visibleRouteDapp, visibleDapps],
  );
  const autoExpandedDapp = useMemo(() => {
    const dappIdFromPath = resolveDappIdFromPath(currentPath, primaryDapps);
    return {
      key: [
        currentPath,
        ...primaryDapps.map((dapp) => `${dapp.id}:${dapp.packageName ?? ''}`),
      ].join('|'),
      id:
        dappIdFromPath ??
        (primaryDapps.length === 1 ? primaryDapps[0].id : null),
    };
  }, [currentPath, primaryDapps]);

  useEffect(() => {
    if (hasInitialized) {
      setIsSettingsMode(isOnSettingsPage);
    }
  }, [isOnSettingsPage, hasInitialized]);

  useEffect(() => {
    setHasInitialized(true);
  }, []);

  useEffect(() => {
    setExpandedDappId(null);
  }, [activePublisher?.publisherUserJoinId]);

  useEffect(() => {
    if (activePublisher?.isFrozen || !publisherVerification.isApproved) {
      setIsAddDappDropdownOpen(false);
    }
  }, [activePublisher?.isFrozen, publisherVerification.isApproved]);

  useEffect(() => {
    const segment = getDappRouteSegmentFromPath(currentPath);

    if (!segment || !canUseStaffDappRoutes) {
      setRouteDapp(null);
      return;
    }

    const visibleDapp = resolveVisibleDappFromRouteSegment(
      segment,
      visibleDapps,
    );
    if (visibleDapp) {
      setRouteDapp(null);
      return;
    }

    let isCancelled = false;

    const loadRouteDapp = async () => {
      try {
        const routeResult = await trpc.resolveDappRouteRef.query({
          ref: segment,
          scope: 'admin',
        });
        if (isCancelled) {
          return;
        }

        if (!('_tag' in routeResult) || !isRight(routeResult)) {
          setRouteDapp(null);
          return;
        }

        const dappResult = await trpc.getDapp.query({
          dappId: routeResult.right.id,
        });
        if (isCancelled) {
          return;
        }

        if ('_tag' in dappResult && isRight(dappResult)) {
          setRouteDapp(dappResult.right);
          return;
        }

        setRouteDapp(null);
      } catch (error) {
        if (!isCancelled) {
          setRouteDapp(null);
          captureAndReportError('Failed to load route dapp:', error);
        }
      }
    };

    void loadRouteDapp();

    return () => {
      isCancelled = true;
    };
  }, [canUseStaffDappRoutes, currentPath, trpc, visibleDapps]);

  // Auto-expand dapp based on the current path.
  useEffect(() => {
    if (autoExpandedDapp.id) {
      setExpandedDappId(autoExpandedDapp.id);
    }
  }, [autoExpandedDapp.id, autoExpandedDapp.key]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideUserDropdown =
        !event.target ||
        !(event.target as Element).closest('[data-dropdown="user"]');
      const isOutsideAddDappDropdown =
        !event.target ||
        !(event.target as Element).closest('[data-dropdown="add-dapp"]');

      if (isOutsideUserDropdown && isDropdownOpen) {
        setIsDropdownOpen(false);
      }

      if (isOutsideAddDappDropdown && isAddDappDropdownOpen) {
        setIsAddDappDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isAddDappDropdownOpen]);

  // Check if connected wallet has already claimed SKR and if wallet is eligible
  useEffect(() => {
    if (!connected || !publicKey) {
      setSkrClaimed(false);
      setWalletEligible(false);
      return;
    }

    const walletAddress = publicKey.toBase58();

    trpc.skrAirdrop.getSkrClaimStatus
      .query({ walletAddress, cluster: SOLANA_CLUSTER })
      .then((result) => {
        setSkrClaimed(result.claimed);
      })
      .catch(() => {
        setSkrClaimed(false);
      });

    trpc.skrAirdrop.checkWalletEligibility
      .query({ walletAddress })
      .then((result) => {
        setWalletEligible(result.eligible);
      })
      .catch(() => {
        setWalletEligible(false);
      });
  }, [connected, publicKey, trpc]);

  const getKybStatus = (): SidebarVerificationCard => {
    if (authState.type !== AuthStateType.Authenticated) {
      return {
        status: 'Loading',
        title: 'Verification',
        badgeText: 'Loading',
        isActionable: false,
        show: false,
      };
    }

    if (!activePublisher) {
      return {
        status: 'Loading',
        title: 'Verification',
        badgeText: 'Loading',
        isActionable: false,
        show: false,
      };
    }

    const isIndividual = activePublisher.publisherType === 'Individual';
    const verificationLabel = isIndividual
      ? 'Identity Verification'
      : 'Business Verification';
    const linkTo = isIndividual
      ? '/dashboard/submit-kyc'
      : '/dashboard/submit-kyb';

    if (activePublisher.kycKybBypassEnabled) {
      return {
        status: 'Approved',
        title: verificationLabel,
        badgeText: 'Completed!',
        linkTo,
        isActionable: false,
        show: false,
      };
    }

    // Use detailed KYB status if available
    if (currentKybStatusData) {
      const flags = getVerificationFlags(currentKybStatusData);
      switch (currentKybStatusData.status) {
        case 'NotStarted':
          if (flags.hasApplicant) {
            return {
              status: 'InProgress',
              title: verificationLabel,
              badgeText: 'Continue',
              linkTo,
              isActionable: true,
              show: true,
            };
          }
          return {
            status: 'NotStarted',
            title: verificationLabel,
            badgeText: VERIFICATION_DEADLINE,
            linkTo,
            isActionable: true,
            show: true,
          };
        case 'InProgress':
          return {
            status: 'InProgress',
            title: verificationLabel,
            badgeText: 'In Progress',
            linkTo,
            isActionable: true,
            show: true,
          };
        case 'UnderReview':
          return {
            status: 'UnderReview',
            title: verificationLabel,
            badgeText: 'Pending',
            linkTo,
            isActionable: false,
            show: true,
          };
        case 'Approved':
          return {
            status: 'Approved',
            title: verificationLabel,
            badgeText: 'Completed!',
            linkTo,
            isActionable: false,
            show: false,
          };
        case 'Rejected':
          return {
            status: 'Rejected',
            title: verificationLabel,
            badgeText: 'Rejected',
            linkTo,
            isActionable: true,
            show: true,
          };
        case 'RequiresAdditionalInfo':
          return {
            status: 'RequiresAdditionalInfo',
            title: verificationLabel,
            badgeText: 'Needs Info',
            linkTo,
            isActionable: true,
            show: true,
          };
      }
    }

    // Fallback to onboarding state
    switch (authState.user.onboardingState) {
      case 'EmailVerification':
      case 'ProfileCreation':
        return {
          status: 'NotStarted',
          title: verificationLabel,
          badgeText: VERIFICATION_DEADLINE,
          linkTo,
          isActionable: true,
          show: true,
        };
      case 'KYB':
        return {
          status: 'RequiresAdditionalInfo',
          title: verificationLabel,
          badgeText: 'Action Required',
          linkTo,
          isActionable: true,
          show: true,
        };
      case 'Complete':
        return {
          status: 'InProgress',
          title: verificationLabel,
          badgeText: 'In Progress',
          linkTo,
          isActionable: true,
          show: true,
        };
      default:
        return {
          status: 'RequiresAdditionalInfo',
          title: verificationLabel,
          badgeText: 'Action Required',
          linkTo,
          isActionable: true,
          show: true,
        };
    }
  };

  const kybStatus = getKybStatus();
  const organizationName =
    activePublisher?.name || user?.fullName || 'App Name';
  const publisherAvatarUrl =
    activePublisher?.avatarThumbnailUrl || activePublisher?.avatarUrl || null;
  const canManageTeam = activePublisher?.canManageTeam;
  const isOnBusinessVerificationPage = currentPath === '/dashboard/submit-kyb';
  const isOnIdentityVerificationPage = currentPath === '/dashboard/submit-kyc';

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handlePublisherSwitch = async (publisherUserJoinId: string) => {
    if (publisherUserJoinId === activePublisher?.publisherUserJoinId) {
      return;
    }

    const nextPublisher = await switchPublisher(publisherUserJoinId);
    if (!nextPublisher) {
      return;
    }

    try {
      if (isOnBusinessVerificationPage || isOnIdentityVerificationPage) {
        if (
          nextPublisher.kycKybBypassEnabled ||
          nextPublisher.kybStatus === 'Approved'
        ) {
          navigate('/dashboard', { replace: true });
          return;
        }

        if (
          isOnBusinessVerificationPage &&
          nextPublisher.publisherType === 'Individual'
        ) {
          navigate('/dashboard/submit-kyc', { replace: true });
          return;
        }

        if (
          isOnIdentityVerificationPage &&
          nextPublisher.publisherType === 'Organization'
        ) {
          navigate('/dashboard/submit-kyb', { replace: true });
          return;
        }
      }

      if (
        (currentPath === '/dashboard/settings/api-keys' ||
          currentPath === '/dashboard/settings/team') &&
        !nextPublisher.canManageTeam
      ) {
        navigate('/dashboard/settings/preferences', { replace: true });
        return;
      }

      const nextDappsResult = await trpc.listDapps.query(undefined);
      if ('_tag' in nextDappsResult && isRight(nextDappsResult)) {
        const nextDapps = nextDappsResult.right;
        const dappIdFromPath = resolveDappIdFromPath(currentPath, visibleDapps);

        if (
          dappIdFromPath &&
          !nextDapps.some((dapp) => dapp.id === dappIdFromPath)
        ) {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      captureAndReportError(
        'Failed to refresh dapps after publisher switch:',
        error,
      );
    } finally {
      setIsDropdownOpen(false);
    }
  };

  const handleSettings = () => {
    setIsSettingsMode(true);
    setIsDropdownOpen(false);
  };

  const handleBackToApp = () => {
    setIsSettingsMode(false);
    const previousPath = navigationHistory.getPreviousNonSettingsPath();
    navigate(previousPath);
    onLinkClick?.();
  };

  const handleMyProfileClick = () => {
    navigate('/dashboard/settings/profile');
    onLinkClick?.();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsDropdownOpen(false);
      onLinkClick?.();
    } catch (error) {
      captureAndReportError('Sign out failed:', error);
      setIsDropdownOpen(false);
    }
  };

  const handleDappToggle = (dappId: string | null) => {
    setExpandedDappId(expandedDappId === dappId ? null : dappId);
  };

  const handleAddDappDropdownToggle = () => {
    setIsAddDappDropdownOpen(!isAddDappDropdownOpen);
  };

  const handleImportDapp = () => {
    setIsImportDialogOpen(true);
  };

  const canAccessSkrClaim = () => {
    if (skrClaimed) return false;
    if (!connected || !publicKey) return false;
    return walletEligible;
  };

  return (
    <div
      className={cn(
        'w-64 text-white flex flex-col justify-between h-full overflow-hidden relative',
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {isSettingsMode ? (
          <SettingsView
            onBackToApp={handleBackToApp}
            onMyProfileClick={handleMyProfileClick}
            shouldAnimate={hasInitialized}
            onLinkClick={onLinkClick}
            canManageTeam={canManageTeam}
            hasPublisherProfile={hasPublisherProfile}
          />
        ) : (
          <motion.div
            key="main"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{
              type: 'spring',
              stiffness: 600,
              damping: 35,
              mass: 0.6,
              velocity: 2,
            }}
            className="absolute inset-0 flex flex-col justify-between w-full h-full"
          >
            <SidebarMainContent
              isLoadingData={isLoadingData}
              organizationName={organizationName}
              publisherAvatarUrl={publisherAvatarUrl}
              isDropdownOpen={isDropdownOpen}
              activePublisherUserJoinId={activePublisher?.publisherUserJoinId}
              accessiblePublishers={accessiblePublishers}
              isSwitchingPublisher={isSwitching}
              onDropdownToggle={handleDropdownToggle}
              onSwitchPublisher={handlePublisherSwitch}
              onSettings={handleSettings}
              onSignOut={handleSignOut}
              hasPublisherProfile={hasPublisherProfile}
              kybStatus={kybStatus}
              isActiveRoute={isActiveRoute}
              canAccessSkrClaim={canAccessSkrClaim()}
              userRole={user?.role}
              userEmail={user?.email}
              currentPath={currentPath}
              onLinkClick={onLinkClick}
              primaryDapps={primaryDapps}
              expandedDappId={expandedDappId}
              onDappToggle={handleDappToggle}
              isAddDappDropdownOpen={isAddDappDropdownOpen}
              onToggleAddDappDropdown={handleAddDappDropdownToggle}
              onImportDapp={handleImportDapp}
              isWalletConnected={connected}
              isPublisherFrozen={activePublisher?.isFrozen === true}
              isPublisherVerificationApproved={publisherVerification.isApproved}
              publisherVerificationMessage={publisherVerification.actionMessage}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {isImportDialogOpen && (
        <ImportDappDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
        />
      )}
    </div>
  );
};
